import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from './ErrorFallback';
import { reportError } from './error-pipeline';
import { ErrorScope } from './types';

export interface ErrorBoundaryProps {
  /**
   * Região da árvore coberta por este boundary. Emitida como `escopo` no evento.
   *
   * Obrigatória, sem default: um default faria um boundary de região herdar o escopo do
   * topo em silêncio ao esquecer a prop, e o painel apontaria a região errada.
   */
  readonly scope: ErrorScope;
  /**
   * Valor cuja mudança tira o boundary do estado de erro. Só age depois de uma falha:
   * sem erro, mudar o valor não remonta a árvore filha.
   */
  readonly resetKey?: unknown;
  readonly children?: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Costura de captura de falha de render. Troca a árvore filha pelo aviso de erro e
 * reporta a falha ao pipeline.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  // Fase de derivação de estado: só liga a flag. O reporte não cabe aqui porque esta fase
  // precisa ser livre de efeito colateral, e é também a única que não recebe o
  // `componentStack`.
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportError(error, {
      origin: 'react_boundary',
      scope: this.props.scope,
      componentStack: errorInfo.componentStack,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && !Object.is(prevProps.resetKey, this.props.resetKey)) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    // A árvore filha é descartada por inteiro; não há tentativa de recuperação parcial,
    // porque o estado do subárvore que lançou é justamente o que não se pode confiar.
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}
